import { capitalize } from "@vue/runtime-dom";
import axios from "axios";
import { $login } from "src/api";
import router from "src/routes/router";
import {
  USER_SUBSCRIPTION,
  getUserSubscriptionString,
  getUserType
} from "src/util/users";

/**
 * @typedef {'Admin'|'Client'} UserType
 *
 * @typedef {Object} AuthState
 * @property {string|null} userId
 * @property {string|null} token
 * @property {User} user
 * @property {UserType} userType
 * @property {import("src/util/users").UserSubscription} subscription
 */

/**
 * @typedef {Object} UserClaim
 * @property {'UserType'} claim
 * @property {String} userClaimId
 * @property {UserType} value
 */

/**
 * @typedef {Object} User
 * @property {string} uid
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} email
 * @property {boolean} trial
 * @property {UserType} userType
 * @property {UserClaim[]} claims
 *
 */

/**
 *
 * @returns {AuthState}
 */
const state = () => ({
  userId: null,
  token: null,
  userType: null,
  subscription: null,
  user: null
});
/**
 * @type {import('vuex').GetterTree<AuthState>}
 */
const getters = {
  isLoggedIn: state => {
    return state.token !== null && state.userId !== null;
  },
  token: state => {
    return state.token;
  },
  isAdmin: state => {
    return state.userType === "Admin";
  },
  isPermanentUser: state => {
    return state.subscription === USER_SUBSCRIPTION.permanent;
  },
  fullName: state => {
    return (
      (state.user &&
      [capitalize(state.user.firstName), capitalize(state.user.lastName)].join(
        " "
      ))
    );
  }
};

/**
 * @type {import('vuex').MutationTree<AuthState>}
 */
const mutations = {
  /**
   * @param {AuthState} state
   * @param {Required<AuthState>} payload
   */
  login(state, payload) {
    state.token = payload.token;
    state.userId = payload.userId;
    state.userType = payload.userType;
    state.subscription = payload.subscription;
    state.user = payload.user;
  },

  logout(state) {
    state.token = null;
    state.userId = null;
    state.userType = null;
    state.subscription = null;
    state.user = null;
  },
  /**
   *
   * @param {AuthState} state
   * @param {UserType} payload
   */
  setUserType(state, payload) {
    state.userType = payload;
  }
};

/**
 * @type {import('vuex').ActionTree<AuthState>}
 */
const actions = {
  /**
   * @typedef {Object} LoginPayload
   * @property {string} email
   * @property {string} password
   * @property {UserType} userType
   *
   * @param {import("vuex").ActionContext} param0
   * @param {LoginPayload} payload
   */
  async login({ commit }, { email, password, userType }) {
    try {
      const { data } = await $login(email, password);
      /**
       * @type {AuthState}
       */
      if (userType !== getUserType(data.user)) {
        throw new Error("Forbidden");
      }
      const payload = {
        token: data.jwtToken.token,
        userId: data.jwtToken.uid,
        userType,
        subscription: getUserSubscriptionString(data.user),
        user: data.user
      };
      commit("login", payload);
      return data.user;
    } catch (error) {
      console.log({ error });
      if (axios.isAxiosError(error) && error.response !== undefined) {
        throw new Error(error.response.data.Status.Description);
      } else throw error;
    }
  },
  logout({ getters, commit }) {
    if (getters["isAdmin"]) {
      router.push({ name: "AdminLogin" });
    } else {
      router.push({ name: "UserLogin" });
    }
    commit("logout");
  }
};

/**
 * @type {import('vuex').Module<AuthState>}
 */
export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
